/** 申请码长度（与后端 ScreenDeviceService.NormalizeCode 一致）。 */
export const SCREEN_CODE_LENGTH = 12;

/** 屏上按 4-4-4 三段显示，输入端同样三段录入（4 位 × 3）。 */
export const SCREEN_CODE_SEGMENT_LENGTH = 4;
export const SCREEN_CODE_SEGMENTS = SCREEN_CODE_LENGTH / SCREEN_CODE_SEGMENT_LENGTH;

/**
 * 申请码字符集：与后端 `ScreenDeviceService.CodeAlphabet` 一致（Crockford Base32，去掉 I/L/O/U）。
 * 输入端只放行这些字符，避免把屏上看错的字符送进接口换来一句「申请不存在」。
 */
const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * 手工输入时的易混淆字符纠正：屏上字体下 O/0、I/1、L/1 肉眼难分，
 * 而生成端根本不会产出 O/I/L/U，因此把它们按 Crockford 惯例折叠到对应数字。
 */
const CONFUSABLE: Record<string, string> = { O: '0', I: '1', L: '1' };

/**
 * 逐字符清洗手工输入：大写化 → 易混字符折叠 → 丢弃字符集之外的字符（含空格/连字符/中文标点）。
 * 用于三段输入框的即时过滤，用户不必自己删连字符或纠正大小写。
 */
export function sanitizeScreenCodeInput(raw: string): string {
  let out = '';
  for (const char of raw.toUpperCase()) {
    const mapped = CONFUSABLE[char] ?? char;
    if (CODE_ALPHABET.includes(mapped)) out += mapped;
    if (out.length >= SCREEN_CODE_LENGTH) break;
  }
  return out;
}

/** 把 12 位编码渲染成屏上的 4-4-4 三段形态（不足 12 位原样返回，便于输入过程预览）。 */
export function formatScreenCode(code: string): string {
  const clean = sanitizeScreenCodeInput(code);
  if (clean.length !== SCREEN_CODE_LENGTH) return clean;
  return `${clean.slice(0, 4)}-${clean.slice(4, 8)}-${clean.slice(8)}`;
}

/**
 * 归一化申请码：与后端同口径（去掉 `HQSC:` 前缀与所有非字母数字字符，统一大写），
 * 并额外折叠 O/I/L 这类生成端不会出现的易混字符。
 */
export function normalizeScreenCode(raw: string): string {
  return sanitizeScreenCodeInput(raw.replace(/^HQSC:/i, ''));
}

/**
 * 从任意扫码/粘贴内容里提取申请码：
 * · 兼容旧版二维码里的确认页 URL（`?c=ABCD-EFGH-JKLM`）→ 取 `c` 参数；
 * · 扫码枪/键盘直出 12 位码（可带连字符或 `HQSC:` 前缀）→ 直接归一化。
 */
export function parseScreenCode(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const byUrl = /[?&]c=([^&#\s]+)/i.exec(trimmed);
  const candidate = byUrl?.[1] ? decodeURIComponent(byUrl[1]) : trimmed;
  const code = normalizeScreenCode(candidate);
  return code.length === SCREEN_CODE_LENGTH ? code : null;
}
